<template>
  <div>
    <div>
      <loading-overlay :value="processingPaymentUpdate" />
      <v-fab-transition hide-on-leave>
        <v-container v-if="showExistingPaymentCards">
          <v-row justify="center">
            <option-card
              :loading="loadingCurrentPayment"
              :class="
                selectedPaymentKey === 'current' &&
                  !confirmChangePaymentDialog &&
                  'selected'
              "
              @click.native="
                logEvent('Selected payment method', {
                  Location: location,
                  Type: 'Used Existing'
                });
                selectPayment('current');
              "
              :icon="
                currentPaymentMethod &&
                currentPaymentMethod.paymentType === 'CREDIT_CARD'
                  ? '$creditcard'
                  : currentPaymentMethod &&
                    currentPaymentMethod.paymentType === 'ACH'
                  ? '$bankaccount'
                  : null
              "
              title="Use Current Payment Method"
              :description="accountText"
            />

            <option-card
              :loading="loadingCurrentPayment"
              :class="confirmChangePaymentDialog && 'selected'"
              @click.native="
                logEvent('Selected payment method', {
                  Location: location,
                  Type: 'Changed Payment Method'
                });
                confirmChangePaymentDialog = true;
                $emit('has-dialog', true);
              "
              icon="mdi-square-edit-outline"
              title="Change Payment Method"
              description="You can use a different credit card or bank account."
            />
          </v-row>
        </v-container>
      </v-fab-transition>
      <v-fab-transition hide-on-leave>
        <v-container v-if="showNewPaymentCards">
          <v-row justify="center">
            <option-card
              icon="$creditcard"
              :title="`${currentPaymentMethod ? 'Confirm' : 'Add'} Credit Card`"
              description="We accept VISA, Mastercard, Discover, and AMEX"
              :class="selectedPaymentKey === 'CC' && 'selected'"
              @click.native="selectPayment('CC')"
            />
            <option-card
              icon="$bankaccount"
              :class="selectedPaymentKey === 'ACH' && 'selected'"
              @click.native="selectPayment('ACH')"
              :title="
                `${currentPaymentMethod ? 'Confirm' : 'Add'} Bank Account`
              "
            />
          </v-row>
        </v-container>
      </v-fab-transition>
      <div
        class="back-nav"
        v-if="showNewPaymentCards && currentPaymentMethod !== null"
      >
        <v-btn text @click="changePaymentMethod = false">
          <v-icon left>mdi-chevron-left</v-icon>
          Use Existing Payment Method
        </v-btn>
      </div>
    </div>

    <v-dialog :value="selectedPayment" persistent max-width="666">
      <foldable-card
        v-if="selectedPaymentKey === 'CC' && selectedPayment"
        non-foldable
        hide-border
        @closing="destroyCCForm()"
        :title="`${currentPaymentMethod ? 'Confirm' : 'Add'} Credit Card`"
        :loading="loadingCC"
      >
        <v-form @submit.prevent="submitCCForm">
          <div class="mb-6">
            <label class="form-label" for="cc-name"
              >Name(as it appears on card)*</label
            >
            <div class="form-input-wrapper">
              <input
                type="text"
                id="cc-name"
                class="form-input regular-input long-input"
                v-model="ccName"
              />
              <v-icon class="ml-2" v-if="ccNameValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
          </div>

          <div
            v-for="field in selectedPayment.fields"
            :key="`${field.fieldKey}-field`"
            class="mb-6"
            :class="`${field.fieldKey}-field`"
          >
            <label class="form-label" :for="`${field.fieldKey}-id`">{{
              field.label
            }}</label>
            <div class="form-input-wrapper">
              <div
                class="form-input hosted-input"
                :class="field.class"
                :id="`${field.fieldKey}-id`"
              ></div>
              <v-icon class="ml-2" v-if="field.isValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
          </div>

          <div class="text-right mt-8">
            <v-btn type="submit" color="primary" :disabled="!isCCFormValid"
              >Save Account
            </v-btn>
          </div>
        </v-form>
      </foldable-card>

      <foldable-card
        v-else-if="selectedPaymentKey === 'ACH' && selectedPayment"
        non-foldable
        hide-border
        @closing="destroyACHForm()"
        :title="`${currentPaymentMethod ? 'Confirm' : 'Add'} Bank Account`"
      >
        <v-form @submit.prevent="submitACHForm">
          <div class="form-input-wrapper mb-6">
            <v-text-field
              class="form-input vuetify-input long-input"
              hide-details
              dense
              filled
              label="Bank Routing Number*"
              v-mask="['#########']"
              v-model="achForm.routingNumber"
            ></v-text-field>
            <v-icon class="ml-2" v-if="achRoutingNumberValid">$valid</v-icon>
            <v-icon class="ml-2" v-else>$initial</v-icon>
          </div>

          <div class="form-input-wrapper mb-6">
            <v-text-field
              class="form-input vuetify-input long-input"
              hide-details
              dense
              filled
              :append-icon="showACHAccountNumber ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showACHAccountNumber = !showACHAccountNumber"
              :type="showACHAccountNumber ? 'text' : 'password'"
              label="Bank Account Number*"
              v-model="achForm.accountNumber"
            ></v-text-field>
            <v-icon class="ml-2" v-if="achAccountNumberValid">$valid</v-icon>
            <v-icon class="ml-2" v-else>$initial</v-icon>
          </div>

          <div class="mb-6">
            <label class="mb-2 d-block header-label">Account Type*:</label>
            <div class="form-input-wrapper">
              <v-radio-group
                class="mt-0 pt-0"
                v-model="achForm.accountType"
                hide-details
                row
              >
                <v-radio label="Checking" value="checking"></v-radio>
                <v-radio label="Savings" value="saving"></v-radio>
              </v-radio-group>
              <v-icon class="ml-2" v-if="achAccountTypeValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
          </div>

          <div class="mb-6">
            <label class="mb-2 d-block header-label">Ownership Type*:</label>
            <div class="form-input-wrapper">
              <v-radio-group
                class="mt-0 pt-0"
                v-model="achForm.ownershipType"
                hide-details
                row
              >
                <v-radio label="Personal" value="personal"></v-radio>
                <v-radio label="Business" value="business"></v-radio>
              </v-radio-group>
              <v-icon class="ml-2" v-if="achOwnershipTypeValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
          </div>

          <div
            class="form-input-wrapper"
            v-if="achForm.ownershipType === 'business'"
          >
            <v-text-field
              class="form-input vuetify-input long-input"
              hide-details
              dense
              filled
              v-model="achForm.businessName"
              label="Business Name*"
            ></v-text-field>
            <v-icon class="ml-2" v-if="achBusinessNameValid">$valid</v-icon>
            <v-icon class="ml-2" v-else>$initial</v-icon>
          </div>

          <div
            class="mb-6 d-flex"
            v-else-if="achForm.ownershipType === 'personal'"
          >
            <div class="form-input-wrapper">
              <v-text-field
                class="form-input vuetify-input medium-input"
                hide-details
                dense
                filled
                v-model="achForm.firstName"
                label="First Name*"
              ></v-text-field>
              <v-icon class="ml-2" v-if="achFirstNameValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
            <div class="form-input-wrapper ml-6">
              <v-text-field
                class="form-input vuetify-input medium-input"
                hide-details
                dense
                filled
                label="Last Name*"
                v-model="achForm.lastName"
              ></v-text-field>
              <v-icon class="ml-2" v-if="achLastNameValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
          </div>

          <v-divider class="my-6"></v-divider>

          <div class="form-input-wrapper mb-6">
            <v-text-field
              class="form-input vuetify-input long-input"
              hide-details
              dense
              filled
              v-model="achForm.billingAddress.streetAddress"
              label="Billing Address 1*"
            ></v-text-field>
            <v-icon class="ml-2" v-if="achStreetAddressValid">$valid</v-icon>
            <v-icon class="ml-2" v-else>$initial</v-icon>
          </div>

          <div class="form-input-wrapper mb-6">
            <v-text-field
              class="form-input vuetify-input long-input"
              hide-details
              dense
              filled
              v-model="achForm.billingAddress.extendedAddress"
              label="Suite, APT(optional)"
            ></v-text-field>
            <v-icon class="ml-2" v-if="achExtendedAddressValid">$valid</v-icon>
            <v-icon class="ml-2" v-else>$initial</v-icon>
          </div>

          <div class="mb-6 d-flex">
            <div class="form-input-wrapper">
              <v-text-field
                class="form-input vuetify-input medium-input"
                hide-details
                dense
                filled
                v-model="achForm.billingAddress.locality"
                label="City*"
              ></v-text-field>
              <v-icon class="ml-2" v-if="achLocalityValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
            <div class="form-input-wrapper ml-6">
              <v-autocomplete
                :items="states"
                class="form-input vuetify-input short-input"
                filled
                dense
                hide-details
                item-value="code"
                item-text="code"
                v-model="achForm.billingAddress.region"
                label="State*"
              ></v-autocomplete>
              <v-icon class="ml-2" v-if="achRegionValid">$valid</v-icon>
              <v-icon class="ml-2" v-else>$initial</v-icon>
            </div>
          </div>

          <div class="form-input-wrapper mb-6">
            <v-text-field
              class="form-input vuetify-input medium-input"
              hide-details
              dense
              filled
              v-model="achForm.billingAddress.postalCode"
              v-mask="['#####', '#####-####']"
              label="Zip Code*"
            ></v-text-field>
            <v-icon class="ml-2" v-if="achPostalCodeValid">$valid</v-icon>
            <v-icon class="ml-2" v-else>$initial</v-icon>
          </div>

          <v-checkbox
            class="mandate-text"
            v-model="achMandateTextConfirmed"
            :label="achMandateText"
            :true-value="achMandateText"
            :false-value="null"
          >
          </v-checkbox>

          <div class="text-right mt-8">
            <v-btn type="submit" color="primary" :disabled="!isACHFormValid"
              >Save Account
            </v-btn>
          </div>
        </v-form>
      </foldable-card>
    </v-dialog>

    <v-dialog v-model="confirmChangePaymentDialog" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="#FE285B">mdi-alert-rhombus</v-icon>
          <v-btn
            icon
            absolute
            top
            right
            @click="confirmChangePaymentDialog = false"
          >
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          All future payments will be charged to this payment method. Please
          confirm you would like to change your default payment method.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmChangePaymentDialog = false">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="confirmedChangePaymentMethod">
            Change Payment Method
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FoldableCard from "@/components/FoldableCard";
import LoadingOverlay from "@/components/LoadingOverlay";
import OptionCard from "@/components/OptionCard";
import { get, post } from "@/services/request";
import { braintreeTokenAPI, paymentMethodAPI } from "@/services/api";

import client from "braintree-web/client";
import hostedFields from "braintree-web/hosted-fields";
import usBankAccount from "braintree-web/us-bank-account";

import cloneDeep from "lodash/cloneDeep";
import hasNotifications from "@/mixins/has-notifications";
import hasConstant from "@/mixins/has-constant";
import { nDigits, zip } from "@/services/rules";
import { mask } from "vue-the-mask";

const payments = {
  CC: {
    fields: [
      {
        label: "Card Number*",
        class: "cc-card-number long-input",
        fieldKey: "number",
        fieldOption: {
          selector: ".cc-card-number"
        }
      },
      {
        label: "CVV*",
        class: "cc-cvv short-input",
        fieldKey: "cvv",
        fieldOption: {
          selector: ".cc-cvv",
          placeholder: "123"
        }
      },
      {
        label: "Exp Date*",
        class: "cc-expiration short-input",
        fieldKey: "expirationDate",
        fieldOption: {
          selector: ".cc-expiration",
          placeholder: "MM / YY"
        }
      },
      {
        label: "Billing Zip Code*",
        class: "cc-zip medium-input",
        fieldKey: "postalCode",
        fieldOption: {
          selector: ".cc-zip",
          placeholder: "12345",
          minlength: 5,
          maxlength: 5
        }
      }
    ]
  },
  ACH: {}
};

const hostedFieldsConfig = {
  styles: {
    input: "braintree-input-class"
    // might need this at some time in the future
    // '.invalid': 'braintree-invalid-class'
  },

  fields: payments.CC.fields.reduce((prev, cur) => {
    prev[cur.fieldKey] = cur.fieldOption;
    return prev;
  }, {})
};

const braintreePromise = get(braintreeTokenAPI)
  .then(response => response && response.data)
  .then(token => client.create({ authorization: token }))
  .catch(err => console.error("Braintree init error", err));

const simpleValidate = val => val && val.trim();

const achMandateText =
  "By clicking [Save Account], I authorize Braintree, a service of PayPal, on behalf of VendorProof (i) to verify my bank account information using bank information and consumer reports and (ii) to debit my bank account.";

const creditCardErrorMessage =
  "There was a problem processing your credit card; please double check your payment information and try again. If this problem continues, please contact your bank for further assistance.";

const descLast4Pattern = /^.+(\d{4})$/;

export default {
  mixins: [hasNotifications, hasConstant],
  directives: {
    mask
  },
  name: "PaymentOptions",
  props: {
    location: {
      type: String,
      default: "Unknown"
    }
  },
  data: () => ({
    selectedPayment: null,
    selectedPaymentKey: null,
    // CC specific
    loadingCC: false,
    ccName: null,
    // ACH specfic
    achForm: {
      accountNumber: null,
      routingNumber: null,
      accountType: null,
      ownershipType: null,
      firstName: null,
      lastName: null,
      businessName: null,
      billingAddress: {
        streetAddress: null,
        extendedAddress: null,
        locality: null,
        region: null,
        postalCode: null
      }
    },
    achMandateText,
    achMandateTextConfirmed: null,
    showACHAccountNumber: true,
    currentPaymentMethod: null,
    confirmChangePaymentDialog: false,
    changePaymentMethod: false,
    loadingCurrentPayment: true,
    processingPaymentUpdate: false
  }),
  computed: {
    ccNameValid() {
      return simpleValidate(this.ccName);
    },
    isCCFormValid() {
      if (this.selectedPaymentKey !== "CC") {
        console.warn(
          "CC payment is not selected. Can't determine CC Form validity"
        );
        return;
      }
      return (
        this.ccNameValid &&
        this.selectedPayment.fields.every(({ isValid }) => isValid)
      );
    },
    achRoutingNumberValid() {
      return (
        simpleValidate(this.achForm.routingNumber) &&
        nDigits("Routing Number", 9)(this.achForm.routingNumber) === true
      );
    },
    achAccountNumberValid() {
      return simpleValidate(this.achForm.accountNumber);
    },
    achAccountTypeValid() {
      return simpleValidate(this.achForm.accountType);
    },
    achOwnershipTypeValid() {
      return simpleValidate(this.achForm.ownershipType);
    },
    achFirstNameValid() {
      return simpleValidate(this.achForm.firstName);
    },
    achLastNameValid() {
      return simpleValidate(this.achForm.lastName);
    },
    achBusinessNameValid() {
      return simpleValidate(this.achForm.businessName);
    },
    achStreetAddressValid() {
      return simpleValidate(this.achForm.billingAddress.streetAddress);
    },
    achExtendedAddressValid() {
      return simpleValidate(this.achForm.billingAddress.extendedAddress);
    },
    achLocalityValid() {
      return simpleValidate(this.achForm.billingAddress.locality);
    },
    achRegionValid() {
      return simpleValidate(this.achForm.billingAddress.region);
    },
    achPostalCodeValid() {
      return (
        simpleValidate(this.achForm.billingAddress.postalCode) &&
        zip(this.achForm.billingAddress.postalCode) === true
      );
    },
    isACHFormValid() {
      if (this.selectedPaymentKey !== "ACH") {
        console.warn(
          "ACH payment is not selected. Can't determine ACH Form validity"
        );
        return;
      }
      return (
        this.achRoutingNumberValid &&
        this.achAccountNumberValid &&
        this.achAccountTypeValid &&
        this.achOwnershipTypeValid &&
        ((this.achForm.ownershipType === "personal" &&
          this.achLastNameValid &&
          this.achFirstNameValid) ||
          (this.achForm.ownershipType === "business" &&
            this.achBusinessNameValid)) &&
        this.achStreetAddressValid &&
        // this is optional
        // this.achExtendedAddressValid &&
        this.achLocalityValid &&
        this.achRegionValid &&
        this.achPostalCodeValid &&
        this.achMandateTextConfirmed
      );
    },
    accountText() {
      if (this.currentPaymentMethod) {
        const paymentType =
          this.currentPaymentMethod.paymentType === "ACH"
            ? "Account Number"
            : "Credit Card";
        return paymentType + ": ***" + this.currentPaymentMethod.lastFour;
      }
      return "";
    },
    showExistingPaymentCards() {
      return (
        this.loadingCurrentPayment ||
        (this.currentPaymentMethod !== null &&
          this.changePaymentMethod === false)
      );
    },
    showNewPaymentCards() {
      return (
        !this.loadingCurrentPayment &&
        (this.currentPaymentMethod === null ||
          this.changePaymentMethod === true)
      );
    }
  },
  created() {
    this.loadCurrentPaymentMethod();
  },
  methods: {
    confirmedChangePaymentMethod() {
      this.confirmChangePaymentDialog = false;
      this.changePaymentMethod = true;
      this.$emit("has-payment", false);
      this.$emit("has-dialog", false);
      this.selectedPaymentKey = null;
    },
    loadCurrentPaymentMethod() {
      get(paymentMethodAPI)
        .then(response => {
          if (response && response.data) {
            this.currentPaymentMethod = response.data;
            this.$emit("has-existing-payment");
          }
        })
        .finally(() => (this.loadingCurrentPayment = false));
    },
    selectPayment(paymentKey) {
      this.selectedPaymentKey = paymentKey;

      if (paymentKey === "CC") {
        this.selectedPayment = cloneDeep(payments[paymentKey]);
        this.initCCForm();
        this.$emit("has-dialog", true);
      } else if (paymentKey === "ACH") {
        this.selectedPayment = cloneDeep(payments[paymentKey]);
        this.initACHForm();
        this.$emit("has-dialog", true);
      } else if (paymentKey === "current") {
        this.$emit("has-payment", true);
      }
    },
    savePayment(type, nonce) {
      this.processingPaymentUpdate = true;
      return post(paymentMethodAPI, {
        nonce: nonce,
        paymentType: type
      })
        .then(() => {
          this.loadingCurrentPayment = true;
          this.changePaymentMethod = false;
          this.selectedPaymentKey = "current";
          this.loadCurrentPaymentMethod();
          this.$emit("has-payment", true);
          this.setPaymentComplete();
        })
        .finally(() => (this.processingPaymentUpdate = false));
    },
    initACHForm() {
      braintreePromise
        .then(clientInstance =>
          usBankAccount.create({
            client: clientInstance
          })
        )
        .then(
          usBankAccountInstance => {
            this.usBankAccountInstance = usBankAccountInstance;
            console.log("Braintree ACH initialized successfully");
          },
          err => {
            console.error("Error initializing braintree ACH", err);
          }
        );
    },
    initCCForm() {
      this.loadingCC = true;
      braintreePromise
        .then(clientInstance =>
          hostedFields.create({
            client: clientInstance,
            ...hostedFieldsConfig
          })
        )
        .then(
          hostedFieldsInstance => {
            this.hostedFieldsInstance = hostedFieldsInstance;
            console.log("Braintree hosted fields initialized successfully");

            hostedFieldsInstance.on("inputSubmitRequest", () =>
              this.submitCCForm()
            );

            hostedFieldsInstance.on(
              "validityChange",
              ({ emittedBy, fields }) => {
                const fieldConfig = this.selectedPayment.fields.find(
                  el => el.fieldKey === emittedBy
                );
                if (!fieldConfig) {
                  console.error(
                    `No field config found for field: ${emittedBy}`
                  );
                  return;
                }

                this.$set(fieldConfig, "isValid", fields[emittedBy].isValid);
              }
            );
          },
          err => {
            console.error("Error initializing braintree hosted fields", err);
          }
        )
        .finally(() => (this.loadingCC = false));
    },
    destroyCCForm() {
      if (this.hostedFieldsInstance) {
        this.hostedFieldsInstance.teardown().then(
          () => console.log("Tear down Braintree hosted fields successfuly"),
          err =>
            console.error("Error tearing down Braintree hosted fields", err)
        );
        this.hostedFieldsInstance = null;
      } else {
        console.warn("no hostedFieldsInstance found");
      }
      this.selectedPayment = null;
      this.ccName = null;
    },
    destroyACHForm() {
      if (this.usBankAccountInstance) {
        this.usBankAccountInstance.teardown().then(
          () => console.log("Tear down Braintree ACH successfuly"),
          err => console.error("Error tearing down ACH", err)
        );
        this.usBankAccountInstance = null;
      } else {
        console.warn("no usBankAccountInstance found");
      }
      this.selectedPayment = null;
    },
    submitCCForm() {
      this.logEvent("Clicked button to save new payment information", {
        Location: this.location || "Unknown",
        Type: "CC"
      });

      // Just to double check, technically neither case is possible
      if (!this.hostedFieldsInstance) {
        console.error(
          "no hostedFieldsInstance found. Can't proceed with form submission"
        );
        return;
      }

      if (!this.isCCFormValid) {
        console.warn("CC Form is not completely valid yet.");
        return;
      }

      this.hostedFieldsInstance
        .tokenize({
          cardholderName: this.ccName
        })
        .then(payload => {
          return this.savePayment(this.selectedPaymentKey, payload.nonce).then(
            () => payload.details.lastFour
          );
        })
        .then(lastFour => {
          this.showMessage(
            `You’ve successfully added credit card *******${lastFour}`
          );
          this.destroyCCForm();
        })
        .catch(err => {
          console.error(creditCardErrorMessage, err);
          this.showMessage({
            mode: "error",
            text: creditCardErrorMessage
          });
        });
    },
    submitACHForm() {
      this.logEvent("Clicked button to save new payment information", {
        Location: this.location,
        Type: "ACH"
      });
      // Just to double check, technically neither case is possible
      if (!this.usBankAccountInstance) {
        console.error(
          "no usBankAccountInstance found. Can't proceed with form submission"
        );
        return;
      }

      if (!this.isACHFormValid) {
        console.warn("ACH Form is not completely valid yet.");
        return;
      }

      this.usBankAccountInstance
        .tokenize({
          bankDetails: this.achForm,
          mandateText: this.achMandateTextConfirmed
        })
        .then(
          payload => {
            this.savePayment(this.selectedPaymentKey, payload.nonce).then(
              () => {
                const last4MatchResult = descLast4Pattern.exec(
                  payload.description
                );

                if (last4MatchResult && last4MatchResult.length > 1) {
                  this.showMessage(
                    `You’ve successfully added bank account *******${last4MatchResult[1]}`
                  );
                } else {
                  this.showMessage(`You’ve successfully added bank account`);
                }
              },
              err => {
                const errorMsg = "Error saving ACH payment.";
                console.error(errorMsg, err);
                this.showMessage({
                  mode: "error",
                  // we are not using the message from err object because the server side is not sending one currently
                  text: errorMsg
                });
              }
            );
            this.destroyACHForm();
          },
          err => {
            console.error("error during tokenization ACH", err);
            const errorMsgs = [];
            if (err.message) errorMsgs.push(err.message);
            if (
              err.details &&
              err.details.originalError &&
              err.details.originalError.details &&
              err.details.originalError.details.originalError
            ) {
              err.details.originalError.details.originalError.forEach(
                ({ message }) => errorMsgs.push(message)
              );
            }
            if (errorMsgs.length)
              this.showMessage({
                mode: "error",
                text: errorMsgs.join(" ")
              });
          }
        );
    }
  },

  components: {
    FoldableCard,
    LoadingOverlay,
    OptionCard
  }
};
</script>

<style lang="sass" scoped>
.v-btn
  font-size: 1rem

.v-btn.v-btn--text
  font-weight: normal
  color: $subtitle

.form-input-wrapper
  display: flex
  align-items: center

.form-input
  background-color: $hosted-field-bgcolor
  color: $grey-text-color
  font-size: 0.875rem
  @at-root input#{&}
    outline: none
  &.regular-input, &.hosted-input
    height: 32px

  &.vuetify-input
    flex-grow: 0

  &.long-input
    width: 312px

  &.medium-input
    width: 160px

  &.short-input
    width: 88px

.cvv-field, .expirationDate-field
  display: inline-block

.cvv-field
  margin-right: 24px

.form-label
  font-size: .75rem
  color: $form-label-color
  font-weight: 600

.mandate-text
  ::v-deep label
    font-size: 14px

.header-label
  font-size: 12px
  color: $subtitle
  font-weight: 600

.v-input--radio-group
  ::v-deep .v-label
    color: $grey-text-color
    font-size: 14px
</style>
<style lang="sass">
// see: https://braintree.github.io/braintree-web/current/module-braintree-web_hosted-fields.html#~styleOptions
.braintree-input-class
  color: $grey-text-color
  font-size: 0.875rem
  font-family: $body-font-family
</style>
